import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';
import client from '../../util/api-client';
import { pushToast, useDispatch } from '../../state';
import type {
  CustomerPaymentCreateInput,
  CustomerPaymentInvoiceGroup,
  CustomerPaymentSearchCustomer,
} from '@repo/api-contract';

type DraftPayment = {
  id: string;
  invoiceCode: string;
  customerCode: string;
  companyName: string;
  reference: string;
  amount: number;
  adjustment: number;
  date: string;
};

const getTodayDateInputValue = () => new Date().toISOString().slice(0, 10);

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 2,
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ letterSpacing: 1, textTransform: 'uppercase' }}
      >
        {label}
      </Typography>
      <Typography variant="subtitle1">{value}</Typography>
    </Box>
  );
}

export default function DraftPaymentsSidebar({
  selectedInvoice,
  searchedCustomer,
  onDraftCreated,
}: {
  selectedInvoice: CustomerPaymentInvoiceGroup | null;
  searchedCustomer: CustomerPaymentSearchCustomer | null;
  onDraftCreated: () => void;
}) {
  const { t } = useTranslation([], {
    keyPrefix: 'team.Components.PaymentReceived',
  });
  const dispatch = useDispatch();
  const [invoiceCode, setInvoiceCode] = useState('');
  const [reference, setReference] = useState('');
  const [amount, setAmount] = useState('');
  const [adjustment, setAdjustment] = useState('');
  const [date, setDate] = useState(getTodayDateInputValue);
  const [drafts, setDrafts] = useState<DraftPayment[]>([]);
  const [creatingPayments, setCreatingPayments] = useState(false);
  const [creatingDraftIds, setCreatingDraftIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [failedDraftIds, setFailedDraftIds] = useState<Set<string>>(
    () => new Set(),
  );

  const selectedInvoiceCode = selectedInvoice?.invoiceCode ?? '';
  const selectedInvoicePayment = selectedInvoice?.payments[0] ?? null;
  const draftCustomerCode =
    selectedInvoicePayment?.customerCode ??
    searchedCustomer?.customerCode ??
    '';
  const draftCompanyName =
    selectedInvoicePayment?.companyName ?? searchedCustomer?.companyName ?? '';
  const resultingBalance = selectedInvoice?.resultingBalance ?? 0;
  const parsedAmount = Number(amount);
  const parsedAdjustment = adjustment === '' ? 0 : Number(adjustment);
  const totalDraftAmount = drafts.reduce((sum, draft) => sum + draft.amount, 0);
  const totalDraftAdjustments = drafts.reduce(
    (sum, draft) => sum + draft.adjustment,
    0,
  );
  const amountError = useMemo(() => {
    if (!amount) return '';
    if (!Number.isFinite(parsedAmount)) return t('Enter a valid amount');
    if (parsedAmount < 1) return t('Amount must be at least $1.00');
    if (selectedInvoice && parsedAmount > resultingBalance) {
      return t('Amount cannot exceed {{amount}}', {
        amount: `$${resultingBalance.toFixed(2)}`,
      });
    }
    return '';
  }, [amount, parsedAmount, resultingBalance, selectedInvoice, t]);
  const canDraft =
    invoiceCode.trim().length > 0 &&
    Boolean(draftCustomerCode) &&
    Boolean(draftCompanyName) &&
    reference.trim().length > 0 &&
    amount.length > 0 &&
    Number.isFinite(parsedAdjustment) &&
    date.length > 0 &&
    !amountError;

  useEffect(() => {
    setInvoiceCode(selectedInvoiceCode);
    setReference('');
    setAmount('');
    setAdjustment('');
    setDate(getTodayDateInputValue());
  }, [selectedInvoiceCode]);

  const handleAmountChange = (value: string) => {
    if (value === '') {
      setAmount('');
      return;
    }

    const nextAmount = Number(value);
    if (!Number.isFinite(nextAmount) || nextAmount < 0) return;

    setAmount(
      selectedInvoice && nextAmount > resultingBalance
        ? resultingBalance.toString()
        : value,
    );
  };

  const handleDraft = () => {
    if (!canDraft) return;

    setDrafts((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        invoiceCode: invoiceCode.trim(),
        customerCode: draftCustomerCode,
        companyName: draftCompanyName,
        reference: reference.trim(),
        amount: parsedAmount,
        adjustment: parsedAdjustment,
        date,
      },
    ]);
    setInvoiceCode('');
    setReference('');
    setAmount('');
    setAdjustment('');
    setDate(getTodayDateInputValue());
    onDraftCreated();
  };

  const handleDeleteDraft = (id: string) => {
    if (creatingDraftIds.has(id)) return;
    setDrafts((prev) => prev.filter((draft) => draft.id !== id));
    setFailedDraftIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const handleCreatePayments = async () => {
    if (drafts.length === 0 || creatingPayments) return;

    const draftsToCreate = [...drafts];
    setCreatingPayments(true);
    setCreatingDraftIds(new Set(draftsToCreate.map((draft) => draft.id)));
    setFailedDraftIds((prev) => {
      const next = new Set(prev);
      draftsToCreate.forEach((draft) => next.delete(draft.id));
      return next;
    });

    const results = await Promise.allSettled(
      draftsToCreate.map(async (draft) => {
        const payload: CustomerPaymentCreateInput = {
          customerCode: draft.customerCode,
          companyName: draft.companyName,
          invoiceCode: draft.invoiceCode,
          payment: draft.amount,
          reference: draft.reference,
          adjustment: draft.adjustment,
          detail: '',
          captureDate: Math.floor(
            new Date(`${draft.date}T00:00:00`).getTime() / 1000,
          ),
        };

        try {
          await client.createCustomerPayment.mutate(payload);
          setDrafts((prev) => prev.filter((item) => item.id !== draft.id));
        } finally {
          setCreatingDraftIds((prev) => {
            const next = new Set(prev);
            next.delete(draft.id);
            return next;
          });
        }
      }),
    );

    const failedDrafts = draftsToCreate.filter(
      (_, index) => results[index]?.status === 'rejected',
    );

    if (failedDrafts.length > 0) {
      console.error('Error creating customer payments', results);
      setFailedDraftIds(new Set(failedDrafts.map((draft) => draft.id)));
      failedDrafts.forEach((draft) => {
        dispatch(
          pushToast({
            message: t('Payment for invoice {{invoiceCode}} failed', {
              invoiceCode: draft.invoiceCode,
            }),
            type: 'error',
            duration: 7000,
          }),
        );
      });
    } else {
      dispatch(
        pushToast({
          message: t(
            draftsToCreate.length === 1
              ? '{{count}} payment recorded'
              : '{{count}} payments recorded',
            { count: draftsToCreate.length },
          ),
          type: 'success',
          duration: 7000,
        }),
      );
    }

    setCreatingPayments(false);
  };

  return (
    <Box
      className="border border-gray-200 border-solid rounded shadow-xl"
      sx={{
        width: 280,
        flexShrink: 0,
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
      }}
    >
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
          {t('Draft Payments')}
        </Typography>
        <Stack spacing={1.5}>
          <TextField
            size="small"
            label={t('Invoice #')}
            value={invoiceCode}
            placeholder={t('Select an invoice')}
            helperText={t('Select invoice from table')}
            InputProps={{ readOnly: true }}
            fullWidth
          />
          <TextField
            size="small"
            label={t('Ref')}
            value={reference}
            onChange={(event) => setReference(event.target.value)}
            fullWidth
          />
          <TextField
            size="small"
            label={t('Amount')}
            type="number"
            value={amount}
            onChange={(event) => handleAmountChange(event.target.value)}
            disabled={!invoiceCode.trim()}
            error={Boolean(amountError)}
            helperText={
              amountError ||
              (selectedInvoice
                ? t('Available: {{amount}}', {
                    amount: `$${resultingBalance.toFixed(2)}`,
                  })
                : t('Manual invoice has no balance limit'))
            }
            inputProps={{
              min: 1,
              max: selectedInvoice ? resultingBalance : undefined,
              step: '0.01',
            }}
            fullWidth
          />
          <TextField
            size="small"
            label={t('Adjustment')}
            type="number"
            value={adjustment}
            onChange={(event) => setAdjustment(event.target.value)}
            disabled={!invoiceCode.trim()}
            inputProps={{ step: '0.01' }}
            fullWidth
          />
          <TextField
            size="small"
            label={t('Date')}
            type="date"
            value={date}
            onChange={(event) => setDate(event.target.value)}
            disabled={!invoiceCode.trim()}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <Button
            variant="outlined"
            color="primary"
            disabled={!canDraft}
            onClick={handleDraft}
            fullWidth
          >
            {t('Draft')}
          </Button>
        </Stack>
      </Box>

      <Stack
        spacing={1}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          pr: 0.5,
        }}
      >
        {drafts.map((draft) =>
          (() => {
            const isCreating = creatingDraftIds.has(draft.id);
            const isFailed = failedDraftIds.has(draft.id);

            return (
              <Chip
                key={draft.id}
                icon={
                  isCreating ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : undefined
                }
                label={`${draft.invoiceCode} | $${draft.amount.toFixed(2)}`}
                color={isFailed ? 'error' : 'primary'}
                onDelete={
                  isCreating ? undefined : () => handleDeleteDraft(draft.id)
                }
                sx={{ justifyContent: 'space-between', borderRadius: 1 }}
              />
            );
          })(),
        )}
      </Stack>

      <Box
        sx={{ mt: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}
      >
        <Stack spacing={2}>
          <SummaryRow
            label={t('Total Credit')}
            value={`$${totalDraftAmount.toFixed(2)}`}
          />
          <SummaryRow
            label={t('Total Adjustments')}
            value={`$${totalDraftAdjustments.toFixed(2)}`}
          />
        </Stack>

        <Button
          variant="contained"
          color="primary"
          disabled={drafts.length === 0 || creatingPayments}
          onClick={handleCreatePayments}
          fullWidth
        >
          {t('Record')}
        </Button>
      </Box>
    </Box>
  );
}
